import i18n from "@/i18n";
import { ApiClient } from "../api/client";
import { Context } from "../contex";
import { EmailHelper } from "../email";
import EventBus from "../event-bus";
import { FieldName } from "./names";

export class MetadataProvider {
    static ready = false
    static fields: FieldMetadata[]

    private static _fieldsMap: Map<string, FieldMetadata>
    private static _apiClient = new ApiClient();

    static async init() {
        if (this.ready) return;
        const metadata = await this.getAllMetadata();
        this.fields = metadata.map(f => ({
            dbName: f.FieldMetadataDbFieldName.toLowerCase(),
            friendlyName: f.FieldMetadataFriendlyFieldName,
            code: f.FieldMetadataSortOrder,
            label: this.getFieldLabel(f),
            type: f.FieldMetadataDisplayType,
            min: f.FieldMetadataNumberFieldMinValue || f.FieldMetadataTextFieldMinLength,
            max: f.FieldMetadataNumberFieldMaxValue || f.FieldMetadataTextFieldMaxLength,
            options: f.FieldMetadataDropdownListData?.map(o => ({ value: o.value, label: o.text })) || [],
            entityType: this.getEntityTypeByCode(f.FieldMetadataSortOrder),
            isMandatory: this.isFieldMandatory(f),
            tokenType: f.FieldMetadataTokenType,
            data: f,
        }));
        this._fieldsMap = new Map(this.fields.reverse().map(f => [f.dbName, f]));
        this.ready = true;
    }

    static getEntityFields(entityType: EntityType) {
        const entityFields = this.fields.filter(f => f.entityType === entityType);
        if (entityType == EntityType.Delivery) {
            const opportunityFields = this.fields.filter(f => f.entityType === EntityType.Opportunity);
            entityFields.push(...opportunityFields.map(f => this.getFieldByName(f.dbName, entityType)!).filter(f => f));
        }
        return entityFields;
    }

    static getFieldByName(name: string, entityType?: EntityType): FieldMetadata | undefined {
        let field = this._fieldsMap.get(name.toLowerCase());
        if (!entityType || !field || entityType == field.entityType) return field;
        return { ...field, entityType, friendlyName: this.getFriendlyName(name, entityType), isMandatory: this.isFieldMandatory(field.data, entityType) };
    }

    static getEntityTypeByCode(code: number) {
        const codePrefix = Math.floor(code / 1000);
        switch (codePrefix) {
            case 11: return EntityType.Organisation;
            case 13: return EntityType.Person;
            case 15: return EntityType.Activity;
            case 17: return EntityType.Product;
            case 19: return EntityType.Opportunity;
            case 21: return EntityType.Delivery;
            case 25: return EntityType.Document;
            case 40: return EntityType.SyncedMeeting;
            case 70: return EntityType.SupportCase;
            default: return EntityType.Undefined;
        }
    }

    static isEntityEnabled(entityType: EntityType) {
        switch (entityType) {
            case EntityType.Activity: return Context.settings.features.activities;
            case EntityType.Opportunity: return Context.settings.features.opportunities;
            case EntityType.Delivery: return Context.settings.features.deliveries;
            case EntityType.SupportCase: return Context.settings.features.supportCases;
            case EntityType.Product: return Context.settings.features.products;
            case EntityType.Meeting: return Context.settings.features.calendarOld;
            case EntityType.SyncedMeeting: return Context.settings.features.calendarNew;
            default: return true;
        }
    }

    static getFieldLabel(field: FieldMetadataDto) {
        if (field.FieldMetadataDbFieldName.match(/^\w+_((p|u)?cust(om)?|chkmark|(gm)?rev|lost)\d+$/i)) {
            return field.FieldMetadataLabel;
        }
        if (field.FieldMetadataFriendlyFieldName.match(/.+OrganisationId/)) {
            return i18n.tc('terms.organisation');
        }
        if ([
            FieldName.OrganisationDivisionName,
            FieldName.OrganisationTerritory,
            FieldName.OrganisationOwner,
            FieldName.OrganisationOwner2,
            FieldName.OrganisationType,
            FieldName.OrganisationStatus,
            FieldName.OrganisationIndustry,
            FieldName.ActivityMainProductService,
            FieldName.OpportunityAssignedTo,
            FieldName.OpportunityAssignedTo2,
            FieldName.OpportunityPersonId,
            FieldName.OpportunityMainProductService,
            FieldName.DeliveryResponsible,
            FieldName.DeliveryPersonId,
            FieldName.SupportCaseTicket,
            FieldName.SupportCaseAssignedTo,
            FieldName.SupportCaseOwner,
            FieldName.SupportCaseMainProductService,
        ].includes(field.FieldMetadataDbFieldName.toLowerCase() as FieldName)) {
            return i18n.tc(`terms.${field.FieldMetadataLabel.toLowerCase()}`);
        }

        return i18n.tc(field.FieldMetadataLabel.toLowerCase());
    }

    static getFieldIcon(fieldName: string) {
        const metadata = this.getFieldByName(fieldName);
        if (!metadata) return '';
        if (metadata.dbName == FieldName.OpportunityOrderValue) return 'payments';
        if (metadata.dbName == FieldName.OpportunityLevelText) return 'sell';
        if ([FieldName.OpportunityNumber, FieldName.DeliveryNumber].includes(metadata.dbName as FieldName)) return 'tag';
        if (metadata.friendlyName.match(/.+OrganisationId/)) return 'domain';
        if (metadata.type == DisplayType.PI) return 'person';
        if (metadata.type == DisplayType.UI) return 'account_circle';
        if (metadata.type == DisplayType.C) return 'help_center';
        if ([DisplayType.D, DisplayType.DI, DisplayType.SD, DisplayType.SDI].includes(metadata.type)) return 'sell';
        if ([DisplayType.I, DisplayType.IC, DisplayType.R].includes(metadata.type)) return 'tag';
        if ([DisplayType.DA, DisplayType.DAT, DisplayType.DT, DisplayType.DTGMT].includes(metadata.type)) return 'event';
        return '';
    }

    static getFriendlyName(fieldName: string, entityType?: EntityType) {
        const metadata = this.getFieldByName(fieldName);
        return this.convertFriendlyName(metadata?.friendlyName || '', entityType);
    }

    static convertFriendlyName(friendlyName: string, entityType?: EntityType) {
        if (entityType == EntityType.Delivery && friendlyName.startsWith('Opportunity')) {
            friendlyName = friendlyName.match(/^Opportunity(Custom|Plus|Memo|Number|Currency|Description|PersonId)/)
                ? `Delivery${friendlyName}`
                : friendlyName.replace('Opportunity', 'Delivery');
        }
        return friendlyName;
    }

    static isFieldMandatory(field: FieldMetadataDto, entityType?: EntityType) {
        return [
            FieldName.OrganisationName,
            FieldName.PersonOrganisationId,
            FieldName.PersonLastName,
            FieldName.ActivityDateTime,
            FieldName.OpportunityOrderDate,
            FieldName.SupportCaseDeadline,
        ].includes(field.FieldMetadataDbFieldName.toLowerCase() as FieldName)
            || field.FieldMetadataCustomFieldMode === CustomFieldMode.Mandatory
            || field.FieldMetadataFieldMode === ExtraFieldMode.Mandatory
            || field.FieldMetadataFieldMode === ExtraFieldMode.UniqueMandatory
            || entityType == EntityType.Delivery && field.FieldMetadataCustomFieldMode == CustomFieldMode.MandatoryWon;
    }

    static isFieldSupported(field: FieldMetadata) {
        return [DisplayType.T, DisplayType.D, DisplayType.DI, DisplayType.SD, DisplayType.DA, DisplayType.DAT, DisplayType.DT, DisplayType.DTGMT].includes(field.type);
    }

    static async getAllMetadata() {
        return await this._apiClient.get('/metadata/fields') as FieldMetadataDto[];
    }

    static validateField(field: FieldMetadata, value: any) {
        if (field.isMandatory && !value) {
            return i18n.tc('please_fill_in_the_value');
        }
        if ([DisplayType.T, DisplayType.M].includes(field.type) && field.min && (value.toString().length < field.min || value.toString().length == 0)) {
            return `${i18n.tc('too_few_chars_min:_')} ${field.min}`;
        }
        if ([DisplayType.T, DisplayType.M].includes(field.type) && field.max && value.toString().length > field.max) {
            return `${i18n.tc('too_many_chars_max:_')} ${field.max}`;
        }
        if ([FieldName.PersonEmail, FieldName.PersonEmail2].includes(field.dbName as FieldName) && value && !EmailHelper.isEmailValid(value)) {
            return i18n.tc('enter_valid_email_adr');
        }

        return null;
    }

    static validateEntity(entity: any, fields: FieldMetadata[]) {
        let valid = true;
        for (let field of fields) {
            const result = this.validateField(field, entity[field.friendlyName]);
            if (result) {
                EventBus.$emit('field-invalid', field, result);
                valid = false;
            }
        }
        return valid;
    }
}

export interface FieldMetadataDto {
    FieldMetadataDbFieldName: string
    FieldMetadataFriendlyFieldName: string
    FieldMetadataSortOrder: number
    FieldMetadataLabel: string
    FieldMetadataDisplayType: DisplayType
    FieldMetadataTextFieldMaxLength: number
    FieldMetadataTextFieldMinLength: number
    FieldMetadataNumberFieldMaxValue?: number
    FieldMetadataNumberFieldMinValue?: number
    FieldMetadataDropdownListData?: FieldListMetadataDto[]
    FieldMetadataCustomFieldMode: CustomFieldMode
    FieldMetadataFieldMode: ExtraFieldMode
    FieldMetadataTokenType: TokenType
}

export interface FieldListMetadataDto {
    text: string
    value: string
}

export enum DisplayType {
    C = 'C',
    CPL = 'CPL',
    D = 'D',
    DA = 'DA',
    DAT = 'DAT',
    DI = 'DI',
    DT = 'DT',
    DTGMT = 'DTGMT',
    EMA = 'EMA',
    GM = 'GM',
    HM = 'HM',
    HTML = 'HTML',
    HX = 'HX',
    I = 'I',
    IC = 'IC',
    IS = 'IS',
    L = 'L',
    LOGA = 'LOGA',
    LOGP = 'LOGP',
    M = 'M',
    ML = 'ML',
    NONE = 'NONE',
    PI = 'PI',
    R = 'R',
    REV = 'REV',
    SD = 'SD',
    SDI = 'SDI',
    T = 'T',
    TELE = 'TELE',
    TM = 'TM',
    UI = 'UI',
    KPI = 'KPI',
    Money = 'Money',
}

export enum CustomFieldMode {
    MandatoryWon = 'MandatoryWon',
    Standard = 'Standard',
    Mandatory = 'Mandatory',
    ReadOnly = 'ReadOnly',
    Hidden = 'Hidden',
    Protected = 'Protected',
}

export enum ExtraFieldMode {
    NotUsed = 'NotUsed',
    Optional = 'Optional',
    Mandatory = 'Mandatory',
    Unique = 'Unique',
    UniqueMandatory = 'UniqueMandatory',
}

export enum TokenType {
    Undefined = 'Undefined',
    String = 'String',
    Integer = 'Integer',
    Float = 'Float',
    Boolean = 'Boolean',
    Array = 'Array',
}
export interface FieldMetadata {
    dbName: string
    friendlyName: string
    code: number
    label: string
    type: DisplayType
    min: number
    max: number
    options: {
        value: string,
        label: string
    }[],
    isMandatory: boolean
    entityType: EntityType
    tokenType: TokenType
    data: FieldMetadataDto
}

export enum EntityType {
    Undefined = 0,
    Organisation = 1,
    Person = 2,
    Activity = 3,
    Product = 4,
    Opportunity = 5,
    Delivery = 6,
    InboundEmail = 7,
    Document = 8,
    User = 9,
    Meeting = 10,
    OutboundEmail = 11,
    OpportunityWonLost = 12,
    SupportCase = 28,
    EmailSupport = 29,
    SyncedMeeting = 40,
    EmailAttachment = 303,
    PersonB2C = 1000,
}